import { useEffect, useRef } from 'react';
import useApi from '../api';
import { JobEvent, WakePlan } from '../services/servicesShared';

/**
 * Live payloads on GET /jobs/{id}/stream (backend/api/services/jobs/stream.py).
 * `event` rows are authoritative and replayable by seq; everything else is
 * advisory — the page re-reads the job view when one arrives.
 */
export type JobStreamMessage =
    | { type: 'event'; event: JobEvent }
    | {
          type: 'status';
          status: 'on' | 'off';
          blocked_on: string | null;
          headline: string | null;
          working: boolean;
          active_wake_id: string | null;
          wake_status: string | null;
      }
    | { type: 'plan'; wake_id: string; plan: WakePlan }
    | { type: 'wake'; status: string; wake_id: string; reason?: string; summary?: string | null }
    | { type: 'state'; state: Record<string, any>; details: Record<string, any>; headline: string | null }
    | { type: 'question'; status?: string; request: Record<string, any> }
    | { type: 'approval'; approval: Record<string, any> };

interface Options {
    /** Seq of the last event already rendered; the stream replays after it. */
    after: number;
    enabled?: boolean;
    onMessage: (message: JobStreamMessage) => void;
    onReconnect?: () => void;
}

const MAX_BACKOFF_MS = 30_000;

/**
 * Dashboard auth is a Supabase bearer token (plus X-Active-Organization-Id
 * for admins), which `EventSource` cannot send, so the stream is consumed
 * with the same fetch helper every other dashboard call uses (`getStream`)
 * and parsed as SSE by hand. Reconnects with backoff from the last seq seen.
 */
export function useJobStream(jobId: string | null | undefined, { after, enabled = true, onMessage, onReconnect }: Options) {
    const { getStream } = useApi();
    const cursorRef = useRef(after);
    const onMessageRef = useRef(onMessage);
    const onReconnectRef = useRef(onReconnect);
    onMessageRef.current = onMessage;
    onReconnectRef.current = onReconnect;
    // Only the initial cursor matters; after that the hook tracks seq itself.
    if (cursorRef.current < after) cursorRef.current = after;

    useEffect(() => {
        if (!jobId || !enabled) return;
        const controller = new AbortController();
        let cancelled = false;
        let attempt = 0;

        const dispatch = (eventType: string, data: string) => {
            let payload: any;
            try {
                payload = JSON.parse(data);
            } catch {
                return;
            }
            if (!payload || typeof payload !== 'object') return;
            const type = payload.type || eventType;
            if (type === 'event') {
                const seq = Number(payload.event?.seq || 0);
                if (seq <= cursorRef.current) return;
                cursorRef.current = seq;
            }
            onMessageRef.current({ ...payload, type } as JobStreamMessage);
        };

        const consumeFrame = (frame: string) => {
            let eventType = 'message';
            const dataLines: string[] = [];
            for (const line of frame.split('\n')) {
                if (!line || line.startsWith(':')) continue;
                const colon = line.indexOf(':');
                const field = colon === -1 ? line : line.slice(0, colon);
                const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
                if (field === 'event') eventType = value;
                else if (field === 'data') dataLines.push(value);
            }
            if (dataLines.length) dispatch(eventType, dataLines.join('\n'));
        };

        const run = async () => {
            while (!cancelled) {
                try {
                    const response = await getStream(`/jobs/${jobId}/stream?after=${cursorRef.current}`, {
                        signal: controller.signal,
                    });
                    if (attempt > 0) onReconnectRef.current?.();
                    attempt = 0;
                    const reader = response.stream.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        let boundary = buffer.indexOf('\n\n');
                        while (boundary !== -1) {
                            consumeFrame(buffer.slice(0, boundary));
                            buffer = buffer.slice(boundary + 2);
                            boundary = buffer.indexOf('\n\n');
                        }
                    }
                } catch (error) {
                    if (cancelled) return;
                }
                if (cancelled) return;
                attempt += 1;
                const wait = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempt, 5));
                await new Promise((resolve) => setTimeout(resolve, wait));
            }
        };

        void run();
        return () => {
            cancelled = true;
            controller.abort();
        };
        // getStream is a stable callback from useApi.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, enabled]);
}
